import React, { useState, useEffect } from 'react';
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom';
import { ShoppingCart, Phone, Menu, X, Facebook, Instagram, Youtube, Twitter, Linkedin, MessageCircle, RefreshCw, ShieldCheck, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useConsent } from './ConsentManager';
import AnaChat from './AnaChat';
import { api, CONTACT, LOGO_URL } from '../lib/api';

function Logo({ small }) {
  // Jerarquía de marca: header público h-16→h-24 (móvil→escritorio), footer h-14
  return (
    <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" aria-label="Natur Autoescuela - Inicio">
      <img src={LOGO_URL} alt="Natur Autoescuela - Aprende simple, eficiente y seguro" className={small ? 'h-14 w-auto' : 'h-16 md:h-24 lg:h-28 w-auto'} />
    </Link>
  );
}

export default function Layout() {
  const { count, total } = useCart();
  const consent = useConsent();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [legalDocs, setLegalDocs] = useState([]);
  const [anaEnabled, setAnaEnabled] = useState(false);
  const [hasBuyables, setHasBuyables] = useState(false);

  useEffect(() => {
    api.getCities().then(setCities).catch(() => {});
    api.getLegalActive().then(setLegalDocs).catch(() => {});
    api.getScmeSettings().then((s) => setAnaEnabled(s.anaEnabled !== false)).catch(() => {});
    api.getProducts().then((ps) => setHasBuyables(ps.some((p) => p.saleMode === 'libre'))).catch(() => {});
  }, []);

  const showCart = count > 0 || hasBuyables;

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/tienda?q=${encodeURIComponent(query)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFBF3]">
      {/* Top header */}
      <header className="bg-[#FDFDF6] border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-6">
          <Logo />
          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md" data-testid="search-form">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar en Natur..."
              className="flex-1 border border-stone-300 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              data-testid="search-input"
            />
            <button type="submit" className="bg-stone-800 text-white px-5 text-sm hover:bg-stone-700 transition-colors" data-testid="search-button">Buscar</button>
          </form>
          <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="hidden lg:flex flex-col items-center text-center group" data-testid="header-whatsapp">
            <MessageCircle className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" />
            <span className="font-bold text-stone-800 mt-1">Escríbenos hoy mismo:</span>
            <span className="text-sm text-stone-500">{CONTACT.phone}</span>
          </a>
          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {/* Nav bar */}
        <div className="max-w-7xl mx-auto px-4 pb-3 hidden md:flex items-center justify-between">
          <span className="text-teal-500 font-bold text-lg w-48 md:w-72 lg:w-80 text-center">Autoescuela Digital</span>
          <nav className="flex items-center gap-8">
            <NavLink to="/" end className={({ isActive }) => `nav-link-natur text-sm font-bold tracking-wide ${isActive ? 'active text-teal-600' : 'text-stone-700'}`} data-testid="nav-inicio">INICIO</NavLink>
            <NavLink to="/tienda" className={({ isActive }) => `nav-link-natur text-sm font-bold tracking-wide ${isActive ? 'active text-teal-600' : 'text-stone-700'}`} data-testid="nav-tienda">TIENDA</NavLink>
          </nav>
          {showCart ? (
            <Link to="/carrito" className="relative flex items-center gap-2 bg-amber-500 hover:bg-amber-600 transition-colors text-white font-bold px-4 py-2 text-sm" data-testid="cart-button">
              {total.toFixed(2).replace('.', ',')} €
              <ShoppingCart className="w-4 h-4" />
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">{count}</span>
            </Link>
          ) : (
            <span className="w-24" aria-hidden="true" />
          )}
        </div>
        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden px-4 pb-4 space-y-3 border-t border-stone-200 pt-3" data-testid="mobile-menu">
            <NavLink to="/" onClick={() => setMobileOpen(false)} className="block font-bold text-stone-700">INICIO</NavLink>
            <NavLink to="/tienda" onClick={() => setMobileOpen(false)} className="block font-bold text-stone-700">TIENDA</NavLink>
            {showCart && (
              <Link to="/carrito" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 bg-amber-500 text-white font-bold px-4 py-2 w-max">
                <ShoppingCart className="w-4 h-4" /> Carrito ({count})
              </Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Social band */}
      <section className="bg-[#B9D9E8] py-10">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-black text-white drop-shadow-sm mb-6">Síganos en redes sociales</h2>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {[
              { icon: Facebook, bg: 'bg-blue-600', href: 'https://www.facebook.com/naturautoescuela/' },
              { icon: Twitter, bg: 'bg-sky-500', href: 'https://x.com/natur_auto_es' },
              { icon: Instagram, bg: 'bg-pink-600', href: 'https://www.instagram.com/naturautoescuela/' },
              { icon: Youtube, bg: 'bg-red-600', href: 'https://www.youtube.com/@naturautoescuela' },
              { icon: Linkedin, bg: 'bg-blue-700', href: '#' },
              { icon: MessageCircle, bg: 'bg-green-500', href: CONTACT.whatsapp },
            ].map(({ icon: Icon, bg, href }, i) => (
              <a key={i} href={href} target="_blank" rel="noreferrer" className={`${bg} text-white w-11 h-11 rounded-lg flex items-center justify-center hover:scale-110 transition-transform`}>
                <Icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F4F4EC] border-t-4 border-blue-900/70 pt-14 pb-8">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Marca */}
          <div className="bg-white rounded-lg border border-stone-200 shadow-sm p-8 flex flex-col items-center">
            <Link to="/" data-testid="footer-logo-link" aria-label="Natur Autoescuela - Inicio">
              <img src={LOGO_URL} alt="Natur Autoescuela - Aprende simple, eficiente y seguro" className="h-24 w-auto" />
            </Link>
            <p className="text-sm text-stone-500 mt-6 leading-7 text-justify">
              Gracias por visitar el área global y legal de NATUR Autoescuela. Es importante para nosotros que usted se sienta satisfech@ y que su compra, o simplemente su visita, sea una experiencia de máxima satisfacción.
            </p>
          </div>

          {/* Mi cuenta */}
          <div className="bg-white rounded-lg border border-stone-200 shadow-sm p-8 text-center">
            <h4 className="font-bold text-stone-800 mb-6 tracking-wide">Mi cuenta</h4>
            <ul className="space-y-4 text-sm text-stone-500">
              <li><Link to="/tienda" className="hover:text-amber-600 transition-colors">Tienda</Link></li>
              <li><Link to="/carrito" className="hover:text-amber-600 transition-colors">Carrito</Link></li>
              <li><Link to="/checkout" className="hover:text-amber-600 transition-colors">Finalizar compra</Link></li>
              <li><Link to="/admin" className="hover:text-amber-600 transition-colors" data-testid="footer-admin-link">Panel MATRIZ</Link></li>
            </ul>
          </div>

          {/* Ciudades */}
          <div className="bg-white rounded-lg border border-stone-200 shadow-sm p-8 text-center">
            <h4 className="font-bold text-stone-800 mb-6 tracking-wide">Autoescuela en tu ciudad</h4>
            <ul className="space-y-4 text-sm text-stone-500">
              {cities.map((c) => (
                <li key={c.id}><Link to={`/${c.slug}`} className="hover:text-amber-600 transition-colors" data-testid={`footer-city-${c.id}`}>Autoescuela en {c.name}</Link></li>
              ))}
            </ul>
          </div>

          {/* Garantías */}
          <div className="bg-white rounded-lg border border-stone-200 shadow-sm p-8 space-y-7">
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                <RefreshCw className="w-5 h-5 text-stone-700" />
              </div>
              <div>
                <h5 className="font-bold text-stone-800 text-sm mb-1">DEVOLUCIONES Y CAMBIOS</h5>
                <p className="text-xs text-stone-500 leading-5">Puede devolver o cambiar el producto.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-stone-700" />
              </div>
              <div>
                <h5 className="font-bold text-stone-800 text-sm mb-1">CALIDAD 100% GARANTIZADA</h5>
                <p className="text-xs text-stone-500 leading-5">Método probado con 80% de aprobados a la primera.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                <Truck className="w-5 h-5 text-stone-700" />
              </div>
              <div>
                <h5 className="font-bold text-stone-800 text-sm mb-1">ACTIVACIÓN INMEDIATA</h5>
                <p className="text-xs text-stone-500 leading-5">Acceso online tras la confirmación del pago.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 mt-12 pt-6 border-t border-stone-300/60 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-stone-600">
          <span>© 2025 Natur Autoescuela®. Todos los derechos reservados.</span>
          <div className="flex gap-5 flex-wrap justify-center" data-testid="footer-legal-links">
            {['condiciones-generales', 'politica-privacidad', 'politica-cookies', 'area-legal']
              .map((s) => legalDocs.find((d) => d.slug === s))
              .filter(Boolean)
              .map((d) => (
              <Link key={d.slug} to={`/legal/${d.slug}`} className="hover:text-amber-600 transition-colors" data-testid={`footer-legal-${d.slug}`}>{d.name}</Link>
            ))}
            <button onClick={() => consent?.openPreferences()} className="hover:text-amber-600 transition-colors" data-testid="footer-privacy-settings">Configuración de Privacidad</button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 mt-5 text-center" data-testid="footer-security-notice">
          <p className="text-sm font-bold text-red-600 leading-relaxed">
            Recuerde que <span className="underline">NUNCA</span> se le solicitarán DATOS BANCARIOS, TARJETAS de Crédito, ni DATOS sensibles vía email/correos electrónicos, ni por teléfono. Esos datos son únicamente ingresados por Usted en el AREA USUARIO o en su área personal de su Blog o Tienda favorita dentro de los dominios de Natur Commodities S.L.
          </p>
        </div>
      </footer>

      {/* Agente ANA (cliente del Chat Provider corporativo, activable desde Ecosistema SCME) */}
      {anaEnabled && <AnaChat />}

      {/* WhatsApp floating */}
      <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="fixed bottom-5 right-5 z-50 bg-green-500 hover:bg-green-600 transition-colors text-white w-14 h-14 rounded-full flex items-center justify-center shadow-xl" data-testid="whatsapp-float">
        <Phone className="w-6 h-6" />
      </a>
    </div>
  );
}
